import React, { useRef, useState, useEffect } from "react";
// import styles from "./FileUploadInput.module.css";
import CommonStyles from "../InputFieldsController/InputFieldsController.module.css"
import { Form } from "react-bootstrap";
import { ThreeCircles } from "react-loader-spinner";
import * as Icon from "react-bootstrap-icons";

import {
  FieldProps,
  getFieldContainerStyle,
  getLabelTextStyle,
  hasRequiredValidation,
} from "../InputFieldsController/InputFieldsController.utils";

interface FileUploadInputProps {
  field: FieldProps;
  handleChange: (fieldId: string, event: any) => void;
  handleBlur: (fieldId: string) => void;
  handleSelect: (fieldId: string) => void;
}

const ImageConfig: Record<string, JSX.Element> = {
  default: <Icon.FileFill />,
  xlsx: <Icon.FileExcel />,
  xls: <Icon.FileExcel />,
};

/**
 * FileUploadInput supports drag & drop file uploads with upload animations.
 * It integrates with the CygnusForm system for layout, validation, and state management.
 *
 * @component
 * @param {Object} props - Props containing field metadata and value handler
 * @param {FieldProps} props.field - File upload field config
 * @param {(fieldId: string, event: any) => void} props.handleChange - Function to update file state
 * @returns {JSX.Element | null} The rendered drag-and-drop file uploader
 */
const FileUploadInput: React.FC<FileUploadInputProps> = ({
  field,
  handleChange,
  handleBlur,
  handleSelect
}) => {
  const {
    id,
    label,
    disabled,
    isValid = true,
    error,
    show = true,
    fileLimit = "single",
    placeholder,
    example,
    uploading = false,
    value,
    selected
  } = field;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [fileList, setFileList] = useState<File[]>(
    Array.isArray(value) ? value : value ? [value] : []
  );

  useEffect(() => {
    // Reset local file list if uploading finishes
    if (!uploading) {
      setFileList([]);
    }
  }, [uploading]);

  if (!show) return null;

  const containerStyle = getFieldContainerStyle(field);
  const isRequired = hasRequiredValidation(field);

  const onFileDrop = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFile = e.target.files?.[0];
    if (!newFile) return;

    let updatedList = [...fileList, newFile];
    if (fileLimit === "single") {
      updatedList = [newFile];
    }

    setFileList(updatedList);
    handleChange(id, fileLimit === "single" ? updatedList[0] : updatedList);
  };

  const fileRemove = (file: File) => {
    const updatedList = fileList.filter((f) => f !== file);
    setFileList(updatedList);
    handleChange(
      id,
      fileLimit === "single" ? updatedList[0] ?? null : updatedList
    );
  };

  const onDragEnter = () => wrapperRef.current?.classList.add("dragover");
  const onDragLeave = () => wrapperRef.current?.classList.remove("dragover");
  const onDrop = () => {
    wrapperRef.current?.classList.remove("dragover");
  };

  return (
    <div style={containerStyle}>
      {label && (
        <Form.Label htmlFor={id} style={getLabelTextStyle(field.textStyle)}>
  {label}
  {isRequired && <span style={{ color: "red", marginLeft: 5 }}>*</span>}
</Form.Label>
      )}

      <div
        ref={wrapperRef}
        className={`drop-file-input ${!isValid ? "is-invalid" : ""} ${selected ? CommonStyles.selectedField : ""}`}
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {uploading && fileList.length > 0 ? (
          <div className="drop-file-input__label">
            <div className="uploading-animation">
              <ThreeCircles
                height="30"
                width="30"
                color="#4fa94d"
                visible={true}
                outerCircleColor="var(--theme1_tertiary)"
                innerCircleColor="var(--theme1_secondary)"
                middleCircleColor="var(--theme1_primary)"
              />
              <p style={{ marginLeft: "10px" }}>Uploading your file...</p>
            </div>
          </div>
        ) : (
          <div className="drop-file-input__label">
            <p>{placeholder || "Drag and drop your file here"}</p>
          </div>
        )}

        <input
          type="file"
          onChange={onFileDrop}
          onBlur={() => handleBlur(id)}
          onFocus={() => handleSelect(id)}
          disabled={disabled}
          multiple={fileLimit === "multiple"}
        />
      </div>

      {!isValid && error && (
        <div className="invalid-feedback d-block">{error}</div>
      )}

      {fileList.map((file, index) => {
        const ext = file.name.split(".").pop()?.toLowerCase() || "default";
        const icon = ImageConfig[ext] || ImageConfig["default"];

        return (
          <li key={index}>
            <div className="icon">{icon}</div>
            <div className="drop-file-preview__item__info">
              <p>{file.name}</p>
              <p>{(file.size / 1000).toFixed(1)} KB</p>
            </div>
            <span
              className="drop-file-preview__item__del"
              onClick={() => fileRemove(file)}
            >
              &times;
            </span>
          </li>
        );
      })}

      {example && <small className="text-muted">Example: {example}</small>}
    </div>
  );
};

export default FileUploadInput;
